import * as yup from 'yup'
import { ValidationError } from 'yup'
import AuthCode from 'react-auth-code-input'
import { FormErrorDisplay } from '@/src/modules/build-a-card/FormErrorDisplay'
import Button from '@/src/common/components/marketing/buttons/Button'
import { styled } from '@/src/stitches.config'
import { FIELD_ERROR_CODE_NUMBER_INVALID } from '@/src/modules/cardholder/VirtualRedemption/strings'
import { clearFormErrorForField } from '@/src/common/utilities/clearFormErrorForField'
import TermsAndPrivacyAgreement from '@/src/common/components/marketing/text/TermsAndPrivacyAgreement'
import { useState } from 'react'

const Wrapper = styled('div', {
    width: '100%',
    maxWidth: 470,
    display: 'flex',
    fd: 'column',
    ai: 'center',
    zIndex: 2,
})

type TConfirmProps = {
    codeSchema: any
    code: string
    setFormErrors: Function
    actionOnSubmit: Function
    hasAgreedToTerms: boolean
}

async function confirmCodeAndSubmit({
    codeSchema,
    code,
    setFormErrors,
    actionOnSubmit,
    hasAgreedToTerms,
}: TConfirmProps) {
    const validCode = await codeSchema.isValid(
        {
            redemptionCode: code,
        },
        { abortEarly: false }
    )

    if (!validCode) {
        return codeSchema
            .validate({ redemptionCode: code }, { abortEarly: false })
            .then(() => setFormErrors([]))
            .catch((e: any) => setFormErrors(e.inner))
    }

    if (!hasAgreedToTerms) {
        const termAgreementRequired: yup.ValidationError = {
            name: 'Term Agreement Required',
            path: 'agreedToTerms',
            message: 'Please complete the checkbox field to continue',
            value: 'term agreement required',
            inner: [],
            errors: [],
        }
        setFormErrors([termAgreementRequired])
        return
    }

    try {
        actionOnSubmit()
        setFormErrors([])
    } catch (e: any) {
        if (e?.code === 'auth/invalid-verification-code') {
            const invalidCodeError: yup.ValidationError = {
                name: 'Invalid Redemption Code',
                path: 'redemptionCode',
                message: 'Invalid redemption code',
                value: 'invalid redemption code',
                inner: [],
                errors: [],
            }
            setFormErrors([invalidCodeError])
        }

        if (e?.code === 'auth/too-many-requests') {
            const invalidError: yup.ValidationError = {
                name: '',
                path: 'redemptionCode',
                message: 'Too many failed attempts. Please try again later.',
                value: 'too many failed attempts',
                inner: [],
                errors: [],
            }
            setFormErrors([invalidError])
        }
    }
}

type TVerificationProps = {
    redemptionCode: string
    setRedemptionCode: Function
    codeLength: number
    buttonText: string
    actionOnSubmit: Function
    buttonWidth?: 'standard' | 'full' | 'fitContent'
    allowedCharacters?: 'alpha' | 'alphanumeric' | 'numeric'
    submissionErrors: ValidationError[]
    setSubmissionErrors: Function
    isLoading?: boolean
}

const VerificationCodeWithButton = ({
    redemptionCode,
    setRedemptionCode,
    codeLength,
    actionOnSubmit,
    buttonText = 'Submit',
    buttonWidth = 'standard',
    allowedCharacters = 'numeric',
    submissionErrors,
    setSubmissionErrors,
    isLoading = false,
}: TVerificationProps) => {
    const [hasAgreedToTerms, setHasAgreedToTerms] = useState<boolean>(false)
    const codeSchema = yup.object({
        redemptionCode: yup
            .string()
            .trim()
            .required(FIELD_ERROR_CODE_NUMBER_INVALID)
            .min(codeLength, `Code must be a ${codeLength}-digit number`),
    })

    return (
        <Wrapper>
            <AuthCode
                length={codeLength}
                onChange={(val: string) => {
                    setRedemptionCode(val.trim())
                    clearFormErrorForField({
                        path: 'redemptionCode',
                        formErrors: submissionErrors,
                        setFormErrors: setSubmissionErrors,
                    })
                }}
                containerClassName="authcode-container"
                inputClassName="authcode-input"
                allowedCharacters={allowedCharacters}
                disabled={isLoading}
            />
            <TermsAndPrivacyAgreement
                setHasAgreedToTerms={setHasAgreedToTerms}
                checkboxSize={'sm'}
                checkboxVariant={'secondary'}
                submissionErrors={submissionErrors}
            />
            <FormErrorDisplay
                formErrors={submissionErrors}
                path={'redemptionCode'}
            />
            <Button
                label={isLoading ? 'Submitting...' : buttonText}
                onClick={() =>
                    confirmCodeAndSubmit({
                        codeSchema: codeSchema,
                        code: redemptionCode,
                        setFormErrors: setSubmissionErrors,
                        actionOnSubmit: actionOnSubmit,
                        hasAgreedToTerms: hasAgreedToTerms,
                    })
                }
                type={'button'}
                width={buttonWidth}
                showArrow={true}
                textTransform="uppercase"
                isLoading={isLoading}
                disabled={isLoading}
                css={{ margin: '20px 0px' }}
            />
        </Wrapper>
    )
}

export default VerificationCodeWithButton
